import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Alert,
  AlertIcon,
  useBreakpointValue,
} from '@chakra-ui/react';

import * as FormFields from './FormFields';

import { ensureConvertersInitialized, handleTranslate } from './utils/TranslationHelper';
import { formatTranslationString } from './utils/formatTranslationString';
import DataTransformer from './utils/DataTransformer';
import TableOperations from './utils/TableOperations';

const BaseAddEditModal = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  dataStructure,
  tableName,
  mainKey = 'id',
}) => {
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [isTranslatorReady, setIsTranslatorReady] = useState(false);
  const modalSize = useBreakpointValue({ base: "sm", md: "2xl", lg: "4xl" });
  const supportedLanguage = ['en', 'zh-Hant', 'zh-Hans']
  const keysWithTranslateButton = ['caption', 'detail', 'tnc'];

  const initializeTranslations = () => {
    return supportedLanguage.map(language => ({
      language,
      image_url: '',
      caption: '',
      detail: '',
      tnc: ''
    }));
  };

  const [data, setData] = useState({ 
    ...initialData, 
    translations: initialData?.translations || initializeTranslations() 
  });

  useEffect(() => {
    if (isOpen) {
      setData({ 
        ...initialData, 
        translations: initialData?.translations || initializeTranslations() 
      });
      setAlert(null);
      setLoading(false);
    }
  }, [isOpen, initialData]);

  useEffect(() => {
    ensureConvertersInitialized().then(() => setIsTranslatorReady(true));
  }, []);

  const handleChange = useCallback((field, value, language = null) => {
    setData(prevData => {
      if (language) {
        return {
          ...prevData,
          translations: prevData.translations.map(translation =>
            translation.language === language
              ? { ...translation, [field]: value }
              : translation
          ),
        };
      } else {
        return { ...prevData, [field]: value };
      }
    });
  }, []);

  const validateFields = useCallback(() => {
    const newErrors = {};
    Object.entries(dataStructure).forEach(([key, field]) => {
      if (field.required && !data[key]) {
        newErrors[key] = `${field.label} is required`;
      } else if (field.validate && !field.validate(data[key])) {
        newErrors[key] = field.validationMessage || `${field.label} is invalid`;
      }
    });

    if (dataStructure.translations && data.translations) {
      data.translations.forEach(translation => {
        Object.entries(dataStructure.translations.fields).forEach(([key, field]) => {
          if (field.required && !translation[key]) {
            newErrors[`translation-${translation.language}-${key}`] = `${field.label} is required`;
          }
        });
      });
    }

    return newErrors;
  }, [data, dataStructure]);

  const handleSave = useCallback(async () => {
    const newErrors = validateFields();
    if (Object.keys(newErrors).length > 0) {
      setAlert({
        title: 'Validation Error',
        description: Object.values(newErrors).join('\n'),
        status: 'error',
      });
      return;
    }

    setLoading(true);
    setAlert(null);

    try {
      let newData = DataTransformer(data, tableName);

      const operation = TableOperations[tableName] || TableOperations.default;

      if (initialData && Object.keys(initialData).includes(mainKey)) {
        // Update operation
        if (operation === TableOperations.default) {
          await operation.update(tableName, data[mainKey], newData, mainKey);
        } else {
          await operation.update(newData);
        }
      } else {
        // Add operation
        if (operation === TableOperations.default) {
          await operation.add(tableName, newData);
        } else {
          await operation.add(newData);
        }
      }

      onClose();
      onSave();
    } catch (error) {
      setAlert({ title: 'Error', description: error.message, status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [data, initialData, mainKey, onClose, onSave, tableName, validateFields]);

  const handleTranslateClick = useCallback(async (field, language, currentValue) => {
    if (!isTranslatorReady) {
      console.log('Translator not ready yet');
      return;
    }
  
    let sourceLanguage, sourceText, targetLanguage;
  
    // Determine the source language based on the target language
    targetLanguage = language;
    if (language === 'zh-Hant') {
      sourceLanguage = 'zh-Hans';
    } else if (language === 'zh-Hans') {
      sourceLanguage = 'zh-Hant';
    } else {
      // For English or any other language, you might want to set a default behavior
      console.error('Unexpected language for translation');
      return;
    }
  
    if (currentValue) {
      // Use the current value if the field has text
      sourceText = currentValue;
    } else {
      // Find the source text from the other language translation
      const sourceTranslation = data.translations.find(t => t.language === sourceLanguage);
      if (!sourceTranslation || !(field in sourceTranslation)) {
        console.error(`Field ${field} not found in source translation`);
        return;
      }
      sourceText = sourceTranslation[field];
    }
  
    if (!sourceText) {
      console.error('No source text available for translation');
      return;
    }
  
    const translatedText = await handleTranslate(sourceText, sourceLanguage, targetLanguage);
    handleChange(field, translatedText, language);
  
  }, [data.translations, isTranslatorReady, handleChange]);

  const renderField = useCallback((key, fieldType, language = null) => {
    const { type, label, options, ...rest } = fieldType;
    const value = language
      ? data.translations.find(t => t.language === language)?.[key]
      : data[key];

    const commonProps = {
      key: language ? `${language}-${key}` : key,
      label,
      value,
      onChange: (value) => handleChange(key, value, language),
      ...rest
    };

    const showTranslateButton = language && keysWithTranslateButton.includes(key);

    if (showTranslateButton) {
      commonProps.showTranslateButton = true;
      commonProps.onTranslate = () => handleTranslateClick(key, language, value);
    }

    switch (type) {
      case 'text':
        return <FormFields.TextField {...commonProps} />;
      case 'textarea':
        return <FormFields.TextareaField {...commonProps} />;
      case 'date':
        return <FormFields.DateField {...commonProps} />;
      case 'image':
        return <FormFields.ImageField {...commonProps} tableName={tableName} />;
      case 'select':
        return <FormFields.SelectField {...commonProps} options={options} />;
      case 'switch':
        return <FormFields.SwitchField {...commonProps} />;
      case 'number':
        return <FormFields.NumberField {...commonProps} />;
      case 'radio':
        return <FormFields.RadioField {...commonProps} options={options} />;
      case 'color':
        return <FormFields.ColorField {...commonProps} />;
      case 'checkbox':
        return <FormFields.CheckboxField {...commonProps} options={options} />;
      default:
        return null;
    }
  }, [data, handleChange, handleTranslateClick]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={modalSize}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{data[mainKey] ? 'Edit' : 'Add'} Item</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {alert && (
            <Alert status={alert.status} mb={4}>
              <AlertIcon />
              <Box>
                <Box fontWeight="bold">{alert.title}</Box>
                <Box>{alert.description}</Box>
              </Box>
            </Alert>
          )}
          {Object.entries(dataStructure).map(([key, field]) => (
            <Box key={key}>
              {renderField(key, field)}
            </Box>
          ))}
          {dataStructure.translations && (
            supportedLanguage.map(language => (
              <Box key={language} mt={4}>
                <Box fontWeight="bold" mb={2}>
                  {formatTranslationString(language)}
                </Box>
                {Object.entries(dataStructure.translations.fields).map(([key, field]) => (
                  <Box key={key}>
                    {renderField(key, field, language)}
                  </Box>
                ))}
              </Box>
            ))
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSave} ml={3} isLoading={loading}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BaseAddEditModal;